/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { useNavigate } from "react-router-dom";
import { accessAPI, TablaFiltrable, Titulo, tituloBase } from "../../shared";

import { Card } from "primereact/card";
import SkeletonListado from "../SkeletonListado";

// Actividades abiertas de los procesos del cliente: una fila por paso actual
// (con pasos en paralelo, más de una por actividad). Cada fila lleva a la
// pantalla de su actividad.
export default function Procesos() {
  const navigate = useNavigate();
  // Título de la pestaña, como en el admin
  useEffect(() => {
    document.title = "Actividades";
    return () => (document.title = tituloBase());
  }, []);

  const toast = useRef(null);
  const [pasos, setPasos] = useState(null);

  useEffect(() => {
    accessAPI(
      "GET",
      "cliente/proceso/actividades",
      null,
      setPasos,
      (r) => {
        setPasos([]);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: r[0]?.msg || "Error al cargar las actividades",
          sticky: true
        });
      }
    );
  }, []);

  // Campos planos para buscar, filtrar, ordenar y exportar
  const filas = (pasos || []).map((paso) => ({
    ...paso,
    _proceso: `${paso.actividad.proceso.nombre} - ${paso.actividad.proceso.nombrefantasia}`,
    _actividad: paso.actividad.actividadtipo.nombre,
    _paso: paso.nombre,
    _certificadoTipo: paso.actividad.proceso.certificadotipo?.nombre || "",
    _dias: paso.fechaInicioPaso
      ? Math.floor((Date.now() - new Date(paso.fechaInicioPaso).getTime()) / 86400000)
      : null
  }));

  // Hasta tener los datos, el esqueleto de la sección (como en el admin)
  // (con el Toast, en el mismo lugar que después, por si falla la carga)
  if (!pasos)
    return (
      <>
        <Toast ref={toast} position="top-center" />
        <SkeletonListado filtros={2} />
      </>
    );

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Titulo
        // Flecha al Inicio, o a donde indique ?volver=
        urlVolver="/"
        etiqueta="Nuevo" neutro
        subtitulo="Actividades abiertas de tus procesos de certificación y el paso en el que está cada una."
      >
        Actividades
      </Titulo>
      <Card css={{ marginBottom: "1em" }}>
        {pasos.length === 0 ? (
          <div css={{ textAlign: "center", padding: "2em 1em", color: "var(--grisTextos)" }}>
            <i className="pi pi-sitemap" css={{ fontSize: "1.5rem" }} />
            <div css={{ fontWeight: 600, color: "rgba(44, 44, 44, 1)", marginTop: "0.5em" }}>No hay actividades abiertas</div>
          </div>
        ) : (
          <TablaFiltrable
            value={filas}
            searchFields={["_proceso", "_actividad", "_paso", "_certificadoTipo"]}
            filtros={[
              { campo: "_actividad", label: "Actividad", labelPlural: "actividades" },
              { campo: "_certificadoTipo", label: "Tipo de certificado", labelPlural: "tipos" }
            ]}
            nombreArchivo="actividades"
            sortMode="multiple"
            removableSort
            onRowClick={(e) => navigate(`/actividades/${e.data.actividad.id}`)}
            css={{ "& .p-datatable-tbody > tr": { cursor: "pointer" } }}
          >
            <Column header="Proceso" field="_proceso" sortable />
            <Column header="Actividad" field="_actividad" sortable />
            <Column
              header="Paso actual"
              field="_paso"
              sortable
              body={(fila) => (
                <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem", alignItems: "flex-start" }}>
                  <span>{fila._paso}</span>
                  {fila._dias != null && (
                    <Tag
                      // Días en el paso: verde hasta 1, ámbar hasta 3, rojo después
                      severity={fila._dias <= 1 ? "success" : fila._dias <= 3 ? "warning" : "danger"}
                      value={fila._dias === 0 ? "hoy" : `hace ${fila._dias} ${fila._dias === 1 ? "día" : "días"}`}
                    />
                  )}
                </div>
              )}
            />
            <Column
              header="Días en el paso actual"
              hidden
              exportValue={(fila) => fila._dias ?? "-"}
            />
          </TablaFiltrable>
        )}
      </Card>
    </>
  );
}
